// Utility functions for WebRTC connections

/**
 * Gets available tools for the AI assistant
 * @param {Array} customTools - Custom tools to add to base tools
 * @returns {Array} Combined tools array
 */
export const getAvailableTools = (customTools = []) => {
  const baseTools = [
    {
      type: 'function',
      name: 'navigateToTab',
      description: 'Navigates to a specific tab',
      parameters: {
        type: 'object',
        properties: {
          tab: {
            type: 'string',
            description: 'The tab to navigate to'
          }
        },
        required: ['tab']
      }
    }
  ]

  // Ensure all custom tools have the required type property
  const processedCustomTools = customTools.map((tool) => ({
    type: 'function',
    ...tool
  }))

  return [...baseTools, ...processedCustomTools]
}

/**
 * Requests microphone permissions and sets up audio stream
 * @returns {Promise<MediaStream|boolean>} The media stream or false if permission denied
 */
export const requestMicrophoneAccess = async () => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
        sampleRate: 48000,
        sampleSize: 16,
        volume: 1.0
      }
    })
    return stream
  } catch (error) {
    console.error('Error accessing microphone:', error)
    return false
  }
}

/**
 * Sets up an audio context for processing audio
 * @returns {Object} The audio context and gain node
 */
export const setupAudioContext = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)({
      sampleRate: 48000,
      latencyHint: 'interactive'
    })

    if (audioContext.state === 'suspended') {
      audioContext.resume()
    }

    // Create gain node for output
    const gainNode = audioContext.createGain()
    gainNode.gain.value = 0.7 // Output volume for AI voice
    gainNode.connect(audioContext.destination)

    return { audioContext, gainNode }
  } catch (error) {
    console.error('Error setting up audio context:', error)
    return { audioContext: null, gainNode: null }
  }
}
